// frontend/src/pages/Company.jsx
// Production-grade company management with comprehensive error handling
import { useState, useEffect, useMemo, useCallback } from "react";
import { useParams, useSearchParams } from "react-router-dom"; // Add useSearchParams
import { useNotify } from "../components/Notify";
import { useCompanyData } from "../hooks/useCompanyData";
import { CompanyHeader } from "../components/company/CompanyHeader";
import { CompanyTabs } from "../components/company/CompanyTabs";
import { CompanyMasterForm } from "../components/company/forms/CompanyMasterForm";
import { CompanyProfileForm } from "../components/company/forms/CompanyProfileForm";
import { CompanyOverview } from "../components/company/tabs/CompanyOverview";
import { CompanyRoles } from "../components/company/tabs/CompanyRoles";
import { CompanyDocuments } from "../components/company/tabs/CompanyDocuments";
import { CompanyAddresses } from "../components/company/tabs/CompanyAddresses";
import { CompanyBankAccounts } from "../components/company/tabs/CompanyBankAccounts";
import { CompanyNotes } from "../components/company/tabs/CompanyNotes";
import { CompanyReviews } from "../components/company/tabs/CompanyReviews";
import { MissingDocsAlert } from "../components/company/MissingDocsAlert";
import RequiredDocsPanel from "../components/RequiredDocsPanel";
import {
  updateCompany,
  updateCompanyProfile,
  softDeleteCompany,
  restoreCompany,
  purgeCompany,
  setCompanyStatus,
  getCountries,
  getDocStatuses,
  getLegalForms,
  getSectors,
  fetchCompanyStatusHistory,
} from "../api";
import { createCountryMap, toYN, parseArray } from "../utils/helpers";
import countriesFallback from "../lib/countriesFallback";
import ErrorBoundary from "../components/ErrorBoundary.jsx";
import { CompanyStatusHistory } from "../components/company/tabs/CompanyStatusHistory";

/**
 * Extract error details from API response
 * Handles both old format (error.missing) and new format (error.data.errors)
 */
function extractMissingDocs(error) {
  if (!error) return null;

  // New format: error.data.errors (from fetchJSON)
  if (error.data && Array.isArray(error.data.errors) && error.data.errors.length > 0) {
    return error.data.errors;
  }

  // Legacy format: error.missing (direct array)
  if (Array.isArray(error.missing) && error.missing.length > 0) {
    return error.missing;
  }

  return null;
}

/**
 * Check if error is a missing documents error
 */
function isMissingDocsError(error) {
  if (!error) return false;

  // Check error title/type
  if (error.data && error.data.title === "docs_missing") return true;
  if (error.message && error.message.includes("Missing required documents")) return true;

  // Check for missing docs array
  return extractMissingDocs(error) !== null;
}

/**
 * Extract user-friendly error message
 */
function getErrorMessage(error) {
  if (!error) return "An unknown error occurred";

  // Missing docs error - don't show generic message
  if (isMissingDocsError(error)) {
    return null; // Alert component will handle display
  }

  // API error with detail
  if (error.data && error.data.detail) {
    return error.data.detail;
  }

  // API error with title
  if (error.data && error.data.title) {
    // Convert snake_case to readable text
    return error.data.title
      .split("_")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  }

  // Standard error message
  if (error.message) {
    return error.message;
  }

  return "An unknown error occurred";
}

export default function CompanyPage() {
  const { id } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = searchParams.get("tab") || "overview"; // ✅ Directly from URL
  const [missingDocs, setMissingDocs] = useState(null);
  const notify = useNotify();

  const handleTabChange = useCallback(
    (tab) => {
      setSearchParams({ tab }, { replace: true }); // ✅ Only update URL
    },
    [setSearchParams],
  );

  // Load company data
  const { loading, error, company, roles, docs, reviews, addresses, bankAccounts, notes, devPermits, taxYears, screenings, requirements, refetch } = useCompanyData(id);

  // Meta data
  const [meta, setMeta] = useState({
    docStatuses: [],
    legalForms: [],
    sectors: [],
  });
  const [countries, setCountries] = useState([]);
  const countryById = useMemo(() => createCountryMap(countries), [countries]);

  // Status history state
  const [statusHistory, setStatusHistory] = useState(null);
  const [statusHistoryError, setStatusHistoryError] = useState(null);

  // Loader for status history
  const loadStatusHistory = useCallback(async () => {
    try {
      setStatusHistoryError(null);
      const history = await fetchCompanyStatusHistory(id);
      setStatusHistory(Array.isArray(history) ? history : []);
    } catch (error) {
      console.error("[CompanyPage] Failed to load status history:", error);
      setStatusHistoryError(error);
    }
  }, [id]);

  // Load status history on mount / company change
  useEffect(() => {
    loadStatusHistory();
  }, [loadStatusHistory]);

  // Load meta data
  useEffect(() => {
    let cancelled = false;

    const loadMeta = async () => {
      try {
        const [statuses, legalForms, sectors] = await Promise.allSettled([getDocStatuses(), getLegalForms(), getSectors()]);

        if (!cancelled) {
          setMeta({
            docStatuses: statuses.status === "fulfilled" ? statuses.value : [],
            legalForms: legalForms.status === "fulfilled" ? legalForms.value : [],
            sectors: sectors.status === "fulfilled" ? sectors.value : [],
          });
        }
      } catch (error) {
        console.warn("Failed to load meta data:", error);
      }
    };

    loadMeta();
    return () => {
      cancelled = true;
    };
  }, []);

  // Load countries
  useEffect(() => {
    let cancelled = false;

    const loadCountries = async () => {
      try {
        const list = await getCountries();

        if (cancelled) return;

        // If API returns incomplete data, merge with fallback
        if (!list || !Array.isArray(list) || list.length < 200) {
          console.warn("API returned incomplete countries, merging with fallback");
          const byIso = new Map();

          (Array.isArray(list) ? list : []).forEach((c) => {
            if (!c || !c.iso2) return;
            byIso.set(String(c.iso2).toUpperCase(), {
              id: c.id != null ? Number(c.id) : null,
              name: c.name || "",
              iso2: String(c.iso2).toUpperCase(),
            });
          });

          countriesFallback.forEach((country, idx) => {
            const iso = String(country.iso2).toUpperCase();
            if (byIso.has(iso)) return;
            byIso.set(iso, {
              id: 10000 + idx,
              name: country.name,
              iso2: iso,
            });
          });

          const merged = [...byIso.values()].sort((a, b) => (a.name || "").localeCompare(b.name || ""));
          setCountries(merged);
          return;
        }

        // Normalize API response
        const normalizedList = list
          .map((c) => ({
            id: c.id != null ? Number(c.id) : null,
            name: c.name || "",
            iso2: (c.iso2 || "").toUpperCase(),
          }))
          .filter((c) => c.name && c.id != null);

        normalizedList.sort((a, b) => (a.name || "").localeCompare(b.name || ""));
        setCountries(normalizedList);
      } catch (error) {
        console.warn("Countries API failed, using fallback:", error);
        if (cancelled) return;

        // Use fallback countries with generated IDs
        const fallbackWithIds = countriesFallback.map((country, index) => ({
          id: index + 1000,
          name: country.name,
          iso2: country.iso2,
        }));
        fallbackWithIds.sort((a, b) => (a.name || "").localeCompare(b.name || ""));
        setCountries(fallbackWithIds);
      }
    };

    loadCountries();
    return () => {
      cancelled = true;
    };
  }, []);

  // Form data for overview tab
  const masterFormData = useMemo(() => {
    if (!company) return {};
    return {
      name: company.name || "",
      ic_name: company.ic_name || "",
      legal_form: company.legal_form || "",
      registration_no: company.registration_no || "",
      country_id: company.country_id || "",
      residency_status: company.residency_status || "",
      managed_by: company.managed_by || "",
      sector: company.sector || "",
      email: company.email || "",
      phone: company.phone || "",
    };
  }, [company]);

  const profileFormData = useMemo(() => {
    if (!company) return {};
    return {
      services: company.services || "",
      business_activity: company.business_activity || "",
      group_member: toYN(company.group_member),
      group_purpose: company.group_purpose || "",
      relationship_started_at: company.relationship_started_at ? company.relationship_started_at.slice(0, 10) : "",
      total_assets: company.total_assets ?? "",
      total_assets_currency: company.total_assets_currency || "",
      transaction_profile: toYN(company.transaction_profile),
      transaction_types: parseArray(company.transaction_types),
      foreign_bank_account: toYN(company.foreign_bank_account),
      fs_last_bookyear: company.fs_last_bookyear || "",
      ubo_origins: Array.isArray(company.ubo_origins) ? company.ubo_origins : company.ubo_origins ? JSON.parse(company.ubo_origins) : [],
      ubo_info_up_to_date: toYN(company.ubo_info_up_to_date),
      pep: toYN(company.pep),
      listed: toYN(company.listed),
      bank_account: toYN(company.bank_account),
      shelf: toYN(company.shelf),
    };
  }, [company]);

  // Action handlers with comprehensive error handling
  const handleMasterFormSubmit = useCallback(
    async (formData) => {
      try {
        await updateCompany(id, formData);
        await refetch();
        notify("Company details saved");
      } catch (error) {
        console.error("[CompanyPage] Master form save failed:", error);
        const message = getErrorMessage(error);
        if (message) notify(message);
        throw error;
      }
    },
    [id, refetch, notify],
  );

  const handleProfileFormSubmit = useCallback(
    async (formData) => {
      try {
        await updateCompanyProfile(id, formData);
        await refetch();
        notify("Profile saved");
      } catch (error) {
        console.error("[CompanyPage] Profile save failed:", error);
        const message = getErrorMessage(error);
        if (message) notify(message);
        throw error;
      }
    },
    [id, refetch, notify],
  );

  const currentStatusMeta = useMemo(() => {
    if (!company || !Array.isArray(statusHistory) || statusHistory.length === 0) {
      return null;
    }

    const sameStatus = statusHistory.filter((entry) => entry.status === company.status);
    if (sameStatus.length === 0) return null;

    // Pick the most recent by effective_date, then created_at
    return sameStatus.reduce((latest, entry) => {
      const latestKey = `${latest.effective_date || ""}|${latest.created_at || ""}`;
      const entryKey = `${entry.effective_date || ""}|${entry.created_at || ""}`;
      return entryKey > latestKey ? entry : latest;
    });
  }, [company, statusHistory]);

  const handleStatusChange = useCallback(
    async (next) => {
      try {
        // Clear any previous errors
        setMissingDocs(null);

        const payload = typeof next === "string" ? { status: next } : next || {};

        await setCompanyStatus(id, payload);
        await Promise.all([refetch(), loadStatusHistory()]);
        notify(`Status changed to ${payload.status}`);
      } catch (error) {
        console.error("[CompanyPage] Status change failed:", error);

        // Check for missing docs error
        if (isMissingDocsError(error)) {
          const missing = extractMissingDocs(error);
          if (missing) {
            setMissingDocs(missing);
            // Scroll to top so user sees the alert
            window.scrollTo({ top: 0, behavior: "smooth" });
          }
        } else {
          // Other errors - show notification
          const message = getErrorMessage(error);
          if (message) notify(message);
        }
      }
    },
    [id, refetch, notify, loadStatusHistory],
  );

  const handleApprove = useCallback(async () => {
    try {
      // Clear any previous errors
      setMissingDocs(null);

      await setCompanyStatus(id, "active");
      await Promise.all([refetch(), loadStatusHistory()]);
      notify("Company activated");
    } catch (error) {
      console.error("[CompanyPage] Approval failed:", error);

      // Check for missing docs error
      if (isMissingDocsError(error)) {
        const missing = extractMissingDocs(error);
        if (missing) {
          setMissingDocs(missing);
          // Scroll to top so user sees the alert
          window.scrollTo({ top: 0, behavior: "smooth" });
        }
      } else {
        // Other errors - show notification
        const message = getErrorMessage(error);
        if (message) notify(message);
      }
    }
  }, [id, refetch, notify, loadStatusHistory]);

  const handleResign = useCallback(async () => {
    try {
      await setCompanyStatus(id, "resigned");
      await refetch();
      notify("Company marked as resigned.");
    } catch (error) {
      console.error("[CompanyPage] Resign failed:", error);
      const message = getErrorMessage(error);
      if (message) notify(message);
    }
  }, [id, refetch, notify]);

  const handleDelete = useCallback(
    async (reason) => {
      try {
        await softDeleteCompany(id, reason);
        await refetch();
        notify("Moved to Trash.");
      } catch (error) {
        console.error("[CompanyPage] Delete failed:", error);
        const message = getErrorMessage(error);
        if (message) notify(message);
      }
    },
    [id, refetch, notify],
  );

  const handleRestore = useCallback(async () => {
    try {
      await restoreCompany(id);
      await refetch();
      notify("Restored.");
    } catch (error) {
      console.error("[CompanyPage] Restore failed:", error);
      const message = getErrorMessage(error);
      if (message) notify(message);
    }
  }, [id, refetch, notify]);

  const handlePurge = useCallback(async () => {
    try {
      await purgeCompany(id);
      notify("Permanently deleted.");
      window.history.back();
    } catch (error) {
      console.error("[CompanyPage] Purge failed:", error);
      const message = getErrorMessage(error);
      if (message) notify(message);
    }
  }, [id, notify]);

  const handleDismissMissingDocs = useCallback(() => {
    setMissingDocs(null);
  }, []);

  const handleUploadDocs = useCallback(() => {
    handleTabChange("documents"); // ✅ Use the handler that updates URL
    setMissingDocs(null);
    setTimeout(() => {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }, 100);
  }, [handleTabChange]); // ✅ Add dependency

  // Loading and error states
  if (loading) {
    return (
      <ErrorBoundary componentName="CompanyPage">
        <div className="container">
          <p>Loading…</p>
        </div>
      </ErrorBoundary>
    );
  }

  if (error) {
    return (
      <ErrorBoundary componentName="CompanyPage">
        <div className="container">
          <p className="error">{error}</p>
        </div>
      </ErrorBoundary>
    );
  }

  if (!company) {
    return (
      <ErrorBoundary componentName="CompanyPage">
        <div className="container">
          <p>Company not found</p>
        </div>
      </ErrorBoundary>
    );
  }

  // Tab content props
  const tabProps = {
    company,
    countries,
    countryById,
    meta,
    notify,
    refetch,
  };

  return (
    <ErrorBoundary componentName="CompanyPage">
      <div className="container">
        <CompanyHeader
          company={company}
          onStatusChange={handleStatusChange}
          onApprove={handleApprove}
          onResign={handleResign}
          onDelete={handleDelete}
          onRestore={handleRestore}
          onPurge={handlePurge}
          loading={loading}
          statusMeta={currentStatusMeta}
        />

        {/* Missing Documents Alert - Prominent placement at top */}
        {missingDocs && missingDocs.length > 0 && <MissingDocsAlert errors={missingDocs} roles={roles} onDismiss={handleDismissMissingDocs} onUpload={handleUploadDocs} />}

        <CompanyTabs activeTab={activeTab} onTabChange={handleTabChange} />

        {activeTab === "overview" && (
          <>
            <CompanyMasterForm data={masterFormData} onSubmit={handleMasterFormSubmit} meta={meta} loading={loading} countries={countries} />
            <CompanyProfileForm data={profileFormData} onSubmit={handleProfileFormSubmit} countries={countries} loading={loading} />
            <RequiredDocsPanel requirements={requirements} docs={docs} roles={roles} country={company?.country_iso2} />
          </>
        )}

        {activeTab === "roles" && <CompanyRoles roles={roles} countries={countries} companyId={id} {...tabProps} />}

        {activeTab === "documents" && <CompanyDocuments docs={docs} requirements={requirements} roles={roles} company={company} companyId={id} {...tabProps} />}

        {activeTab === "addresses" && <CompanyAddresses addresses={addresses} roles={roles} companyId={id} {...tabProps} />}

        {activeTab === "bank" && <CompanyBankAccounts bankAccounts={bankAccounts} companyId={id} {...tabProps} />}

        {activeTab === "notes" && <CompanyNotes notes={notes} companyId={id} {...tabProps} />}

        {activeTab === "reviews" && (
          <CompanyReviews reviews={reviews} devPermits={devPermits} taxYears={taxYears} screenings={screenings} company={company} companyId={id} {...tabProps} />
        )}

        {activeTab === "statusHistory" && (
          <ErrorBoundary componentName="CompanyStatusHistory">
            <CompanyStatusHistory history={statusHistory || []} error={statusHistoryError} />
          </ErrorBoundary>
        )}
      </div>
    </ErrorBoundary>
  );
}
